// src/services/ConfigService.js
import appConfig from '../constants/appConfig.json';

const CONFIG_CACHE_KEY = 'crm_frontend_config';
const CONFIG_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const LOCAL_OVERRIDE_KEY = 'crm_backend_url_override';

/**
 * ConfigService manages system-wide frontend configuration
 * Fetches from server on startup, caches locally, supports local overrides
 */
class ConfigService {
    constructor() {
        this.config = null;
        this.lastFetch = 0;
        this.isInitialized = false;
    }

    /**
     * Initialize from the localStorage cache without hitting the network.
     * Returns true when a cached config was loaded (caller should then
     * refresh() in the background), false when there is no usable cache.
     */
    initializeFromCache() {
        const cached = localStorage.getItem(CONFIG_CACHE_KEY);
        if (!cached) return false;

        try {
            const { config, timestamp } = JSON.parse(cached);
            if (!config || !config.backendUrl) return false;

            this.config = config;
            this.lastFetch = timestamp || 0;
            this.isInitialized = true;
            console.log('📦 ConfigService initialized from cached config');
            return true;
        } catch (e) {
            console.warn('Invalid cached config, falling back to server fetch');
            return false;
        }
    }

    /**
     * Initialize configuration by fetching from server
     * This should be called before the app starts making API calls
     */
    async initialize() {
        try {
            await this.fetchServerConfig();
            this.isInitialized = true;
            console.log('✅ ConfigService initialized with server config');
        } catch (error) {
            console.warn('⚠️ Failed to fetch server config, using fallback:', error.message);
            this.config = this.getFallbackConfig();
            this.isInitialized = true;
        }
    }

    /**
     * Fetch configuration from server
     */
    async fetchServerConfig() {
        // Use a direct fetch to avoid circular dependency with API service
        const envUrl = process.env.NODE_ENV === 'production' 
            ? (appConfig.environments?.production || appConfig.baseUrl)
            : (appConfig.environments?.development || appConfig.baseUrl);
        const defaultUrl = process.env.REACT_APP_BASE_URL || envUrl || 'http://localhost:5050';
        
        const response = await fetch(`${defaultUrl}/api/v1/system/frontend-config`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            throw new Error(`Server responded with status ${response.status}`);
        }

        const data = await response.json();
        
        if (data.config && data.config.iv && data.config.data) {
            try {
                // Decrypt the configuration
                const CONFIG_SECRET = 'AurikshaCRM-Frontend-Config-Key!';
                const enc = new TextEncoder();
                const keyMaterial = await window.crypto.subtle.importKey(
                    'raw', 
                    enc.encode(CONFIG_SECRET.padEnd(32, ' ')), 
                    { name: 'AES-GCM' }, 
                    false, 
                    ['decrypt']
                );

                const decodeBase64 = (str) => Uint8Array.from(atob(str), c => c.charCodeAt(0));
                
                const iv = decodeBase64(data.config.iv);
                const authTag = decodeBase64(data.config.authTag);
                const encryptedData = decodeBase64(data.config.data);

                const ciphertext = new Uint8Array(encryptedData.length + authTag.length);
                ciphertext.set(encryptedData, 0);
                ciphertext.set(authTag, encryptedData.length);

                const decryptedBuffer = await window.crypto.subtle.decrypt(
                    { name: 'AES-GCM', iv: iv },
                    keyMaterial,
                    ciphertext
                );

                const dec = new TextDecoder();
                this.config = JSON.parse(dec.decode(decryptedBuffer));
            } catch(e) {
                console.error('Failed to decrypt config:', e);
                throw new Error('Failed to decrypt configuration');
            }
        } else {
            // Fallback for unencrypted payload during transition
            this.config = data.config;
        }

        this.lastFetch = Date.now();
        
        // Store in localStorage for offline access
        localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify({
            config: this.config,
            timestamp: this.lastFetch
        }));

        return this.config;
    }

    /**
     * Get fallback configuration when server is unreachable
     */
    getFallbackConfig() {
        // Try cached config first
        const cached = localStorage.getItem(CONFIG_CACHE_KEY);
        if (cached) {
            try {
                const { config, timestamp } = JSON.parse(cached);
                // Use cached config even if expired when server is unreachable
                console.log('📦 Using cached config as fallback');
                return config;
            } catch (e) {
                console.warn('Invalid cached config');
            }
        }

        const envUrl = process.env.NODE_ENV === 'production' 
            ? (appConfig.environments?.production || appConfig.baseUrl)
            : (appConfig.environments?.development || appConfig.baseUrl);

        // Ultimate fallback
        return {
            backendUrl: appConfig.environments?.production || appConfig.baseUrl || 'http://localhost:5050',
            devBackendUrl: appConfig.environments?.development || 'http://localhost:5050',
            allowLocalOverride: true,
            environment: 'fallback',
            version: appConfig.version || '1.0.0',
            timestamp: Date.now()
        };
    }

    /**
     * Whether the app is currently running on a development host (localhost).
     * Resolution is driven by the browser's own hostname so a production client
     * (crm.auriksha.com) can NEVER be redirected to a dev/localhost backend, no
     * matter what is stored in the shared system config.
     */
    isDevelopmentHost() {
        if (typeof window === 'undefined' || !window.location) {
            return process.env.NODE_ENV !== 'production';
        }
        const host = window.location.hostname;
        return (
            host === 'localhost' ||
            host === '127.0.0.1' ||
            host === '0.0.0.0' ||
            host === '::1' ||
            host.endsWith('.local')
        );
    }

    /** Reject any localhost/dev URL for a production client. */
    sanitizeProdUrl(url) {
        const prodDefault = appConfig.environments?.production || appConfig.baseUrl || '';
        if (!url || /localhost|127\.0\.0\.1|0\.0\.0\.0|::1/i.test(url)) {
            return prodDefault;
        }
        return url;
    }

    /**
     * Get the current backend URL to use.
     * Environment-keyed by hostname:
     *   - development (localhost): local override > config.devBackendUrl > appConfig.development
     *   - production (any other host): sanitized config.backendUrl > appConfig.production
     * The two paths never cross, so a dev change can't affect production and vice versa.
     */
    getBackendUrl() {
        const isDev = this.isDevelopmentHost();

        if (isDev) {
            // Per-browser override is honored in development only.
            if (this.config?.allowLocalOverride !== false) {
                const override = localStorage.getItem(LOCAL_OVERRIDE_KEY);
                if (override && override.trim()) {
                    console.log('🔧 Using local backend URL override');
                    return override.trim();
                }
            }
            return (
                process.env.REACT_APP_BASE_URL ||
                this.config?.devBackendUrl ||
                appConfig.environments?.development ||
                'http://localhost:5050'
            );
        }

        // Production clients: never fall through to a localhost URL.
        return this.sanitizeProdUrl(this.config?.backendUrl) ||
            appConfig.environments?.production ||
            appConfig.baseUrl;
    }

    /**
     * Get current configuration
     */
    getConfig() {
        return this.config || this.getFallbackConfig();
    }

    /**
     * Set local backend URL override (if allowed by server config)
     */
    setLocalBackendOverride(url) {
        if (!this.config?.allowLocalOverride) {
            console.warn('Local backend URL override is disabled by server configuration');
            return false;
        }

        if (url && url.trim()) {
            localStorage.setItem(LOCAL_OVERRIDE_KEY, url.trim());
            console.log('🔧 Local backend URL override set:', url);
        } else {
            localStorage.removeItem(LOCAL_OVERRIDE_KEY);
            console.log('🔧 Local backend URL override removed');
        }
        
        return true;
    }

    /**
     * Clear local backend URL override
     */
    clearLocalBackendOverride() {
        localStorage.removeItem(LOCAL_OVERRIDE_KEY);
        console.log('🔧 Local backend URL override cleared');
    }

    /**
     * Check if local override is active
     */
    hasLocalOverride() {
        // Overrides only take effect on development hosts.
        return !!(this.isDevelopmentHost() && this.config?.allowLocalOverride && localStorage.getItem(LOCAL_OVERRIDE_KEY));
    }

    /**
     * Refresh configuration from server
     */
    async refresh() {
        try {
            await this.fetchServerConfig();
            console.log('🔄 Configuration refreshed from server');
            return true;
        } catch (error) {
            console.warn('Failed to refresh config:', error.message);
            return false;
        }
    }

    /**
     * Check if config needs refresh (older than TTL)
     */
    needsRefresh() {
        return Date.now() - this.lastFetch > CONFIG_CACHE_TTL;
    }
}

// Export singleton instance
export default new ConfigService();